import {
  Content,
  GoogleGenAI,
  LiveCallbacks,
  LiveClientToolResponse,
  LiveConnectConfig,
  LiveServerContent,
  LiveServerMessage,
  LiveServerToolCall,
  LiveServerToolCallCancellation,
  Part,
  Session,
} from "@google/genai";

import { EventEmitter } from "eventemitter3";
import { difference } from "lodash";
import { LiveClientOptions, StreamingLog } from "./types";
import { base64ToArrayBuffer } from "./utils";
import { AIUsageTracker } from "./usage-tracker";

/**
 * Event types that can be emitted by the GenAILiveClient.
 * Each event corresponds to a specific message from GenAI or client state change.
 */
export interface LiveClientEventTypes {
  // Emitted when audio data is received
  audio: (data: ArrayBuffer) => void;
  // Emitted when the connection closes
  close: (event: CloseEvent) => void;
  // Emitted when content is received from the server
  content: (data: LiveServerContent) => void;
  // Emitted when an error occurs
  error: (error: ErrorEvent) => void;
  // Emitted when the server interrupts the current generation
  interrupted: () => void;
  // Emitted for logging events
  log: (log: StreamingLog) => void;
  // Emitted when the connection opens
  open: () => void;
  // Emitted when the initial setup is complete
  setupcomplete: () => void;
  // Emitted when a tool call is received
  toolcall: (toolCall: LiveServerToolCall) => void;
  // Emitted when a tool call is cancelled
  toolcallcancellation: (toolcallCancellation: LiveServerToolCallCancellation) => void;
  // Emitted when the current turn is complete
  turncomplete: () => void;
}

/**
 * Event-emitting class that manages the connection to the websocket and emits
 * events to the rest of the application.
 */
export class GenAILiveClient extends EventEmitter<LiveClientEventTypes> {
  protected client: GoogleGenAI;
  private usageTracker: AIUsageTracker | null = null;
  private sessionId: string | null = null;
  private audioStartTime: number = 0;
  private isReceivingAudio: boolean = false;

  private _status: "connected" | "disconnected" | "connecting" = "disconnected";
  public get status() {
    return this._status;
  }

  private _session: Session | null = null;
  public get session() {
    return this._session;
  }

  private _model: string | null = null;
  public get model() {
    return this._model;
  }

  protected config: LiveConnectConfig | null = null;

  public getConfig() {
    return { ...this.config };
  }

  constructor(options: LiveClientOptions, usageTracker?: AIUsageTracker) {
    super();
    this.client = new GoogleGenAI(options);
    this.usageTracker = usageTracker || null;
    this.send = this.send.bind(this);
    this.onopen = this.onopen.bind(this);
    this.onerror = this.onerror.bind(this);
    this.onclose = this.onclose.bind(this);
    this.onmessage = this.onmessage.bind(this);
  }

  public setUsageTracker(tracker: AIUsageTracker) {
    this.usageTracker = tracker;
  }

  protected log(type: string, message: StreamingLog["message"]) {
    const log: StreamingLog = {
      date: new Date(),
      type,
      message,
    };
    this.emit("log", log);
  }

  async connect(model: string, config: LiveConnectConfig, feature?: string): Promise<boolean> {
    if (this._status === "connected" || this._status === "connecting") {
      return false;
    }

    // Check quota before connecting
    if (this.usageTracker) {
      const quota = await this.usageTracker.checkQuota();
      if (quota && !quota.withinQuota) {
        console.warn("User has exceeded quota limits");
        this.emit("error", new ErrorEvent("error", { message: "Quota exceeded" }));
        return false;
      }
    }

    this._status = "connecting";
    this.config = config;
    this._model = model;

    // Generate session ID and start tracking
    this.sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    if (this.usageTracker) {
      this.usageTracker.startSession(this.sessionId, feature, model);
    }

    const callbacks: LiveCallbacks = {
      onopen: this.onopen,
      onmessage: this.onmessage,
      onerror: this.onerror,
      onclose: this.onclose,
    };

    try {
      this._session = await this.client.live.connect({
        model,
        config,
        callbacks,
      });
    } catch (e) {
      console.error("Error connecting to GenAI Live:", e);
      this._status = "disconnected";
      if (this.usageTracker) {
        await this.usageTracker.endSession();
      }
      return false;
    }

    this._status = "connected";
    return true;
  }

  public async disconnect() {
    if (!this.session) {
      return false;
    }
    this.session?.close();
    this._session = null;
    this._status = "disconnected";

    // End usage tracking session
    if (this.usageTracker) {
      await this.usageTracker.endSession();
    }

    this.log("client.close", `Disconnected`);
    return true;
  }

  protected onopen() {
    this.log("client.open", "Connected");
    this.emit("open");
  }

  protected onerror(e: ErrorEvent) {
    this.log("server.error", e.message);
    this.emit("error", e);
  }

  protected onclose(e: CloseEvent) {
    this.log(`server.close`, `disconnected ${e.reason ? `with reason: ${e.reason}` : ``}`);
    this.emit("close", e);
  }

  protected async onmessage(message: LiveServerMessage) {
    if (message.setupComplete) {
      this.log("server.send", "setupComplete");
      this.emit("setupcomplete");
      return;
    }
    if (message.toolCall) {
      this.log("server.toolCall", message);
      this.emit("toolcall", message.toolCall);
      return;
    }
    if (message.toolCallCancellation) {
      this.log("server.toolCallCancellation", message);
      this.emit("toolcallcancellation", message.toolCallCancellation);
      return;
    }

    // Handle content updates including interruptions and turn completion
    if (message.serverContent) {
      const { serverContent } = message;
      if ("interrupted" in serverContent) {
        this.log("server.content", "interrupted");
        this.emit("interrupted");
        return;
      }
      if ("turnComplete" in serverContent) {
        this.log("server.content", "turnComplete");
        this.emit("turncomplete");

        // Increment turn count
        if (this.usageTracker) {
          this.usageTracker.incrementTurn();
        }
      }

      if ("modelTurn" in serverContent) {
        let parts: Part[] = serverContent.modelTurn?.parts || [];

        // Handle audio parts
        const audioParts = parts.filter(
          p => p.inlineData && p.inlineData.mimeType?.startsWith("audio/pcm")
        );
        const base64s = audioParts.map(p => p.inlineData?.data);

        // Strip audio parts from the modelTurn
        const otherParts = difference(parts, audioParts);

        base64s.forEach(b64 => {
          if (b64) {
            const data = base64ToArrayBuffer(b64);
            this.emit("audio", data);
            this.log(`server.audio`, `buffer (${data.byteLength})`);

            // Track audio output
            if (this.usageTracker) {
              // PCM audio at 24kHz, 16-bit = 48000 bytes per second
              const audioSeconds = data.byteLength / 48000;
              this.usageTracker.updateUsage({
                audioSeconds,
                type: "output",
              });
            }
          }
        });
        if (!otherParts.length) {
          return;
        }

        parts = otherParts;

        const content: { modelTurn: Content } = { modelTurn: { parts } };
        this.emit("content", content);
        this.log(`server.content`, message);

        // Track text tokens (rough estimate)
        if (this.usageTracker) {
          const textParts = parts.filter(p => "text" in p);
          if (textParts.length > 0) {
            const text = textParts.map(p => (p as any).text).join(" ");
            // Rough token estimate: ~4 characters per token
            const tokens = Math.ceil(text.length / 4);
            this.usageTracker.updateUsage({
              tokens,
              type: "output",
            });
          }
        }
      }
    } else {
      console.log("received unmatched message", message);
    }
  }

  /**
   * Send realtimeInput, this is base64 chunks of "audio/pcm" and/or "image/jpg"
   */
  sendRealtimeInput(chunks: Array<{ mimeType: string; data: string }>) {
    let hasAudio = false;
    let hasVideo = false;
    let totalAudioBytes = 0;

    for (const ch of chunks) {
      this.session?.sendRealtimeInput({ media: ch });
      if (ch.mimeType.includes("audio")) {
        hasAudio = true;
        // Track input audio
        const audioBytes = (ch.data.length * 3) / 4; // Base64 to bytes
        totalAudioBytes += audioBytes;
      }
      if (ch.mimeType.includes("image")) {
        hasVideo = true;
      }
      if (hasAudio && hasVideo) {
        break;
      }
    }

    // Track input audio usage
    if (this.usageTracker && totalAudioBytes > 0) {
      // PCM audio at 24kHz, 16-bit = 48000 bytes per second
      const audioSeconds = totalAudioBytes / 48000;
      this.usageTracker.updateUsage({
        audioSeconds,
        type: "input",
      });
    }

    const message =
      hasAudio && hasVideo ? "audio + video" : hasAudio ? "audio" : hasVideo ? "video" : "unknown";
    this.log(`client.realtimeInput`, message);
  }

  /**
   * Send a response to a function call
   */
  sendToolResponse(toolResponse: LiveClientToolResponse) {
    if (toolResponse.functionResponses && toolResponse.functionResponses.length) {
      this.session?.sendToolResponse({
        functionResponses: toolResponse.functionResponses,
      });
      this.log(`client.toolResponse`, toolResponse);
    }
  }

  /**
   * Send normal content parts such as { text }
   */
  send(parts: Part | Part[], turnComplete: boolean = true) {
    this.session?.sendClientContent({ turns: parts, turnComplete });
    this.log(`client.send`, {
      turns: Array.isArray(parts) ? parts : [parts],
      turnComplete,
    });

    // Track input text tokens
    if (this.usageTracker) {
      const partsArray = Array.isArray(parts) ? parts : [parts];
      const textParts = partsArray.filter(p => "text" in p);
      if (textParts.length > 0) {
        const text = textParts.map(p => (p as any).text).join(" ");
        // Rough token estimate: ~4 characters per token
        const tokens = Math.ceil(text.length / 4);
        this.usageTracker.updateUsage({
          tokens,
          type: "input",
        });
      }
    }
  }
}
